import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Zap, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_video-forge-417/artifacts/8amlg66h_Logo%20futuriste%20de%20ViralSynth.com.png";

const ERROR_LABELS = {
  google_denied: "Connexion Google annulee",
  google_no_code: "Code Google manquant",
  token_exchange: "Echec de l'echange de jeton Google",
  userinfo_failed: "Impossible de recuperer le profil Google",
  userinfo_exception: "Erreur reseau lors de la recuperation du profil",
  no_email: "Aucun email retourne par Google",
  upsert_failed: "Erreur de creation du compte",
  oauth_error: "Erreur d'authentification Google",
  google_auth_failed: "Authentification Google echouee",
};

export default function AuthPage({ mode = 'login' }) {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Surface OAuth error from URL params (set by /api/auth/google/callback redirect)
  useEffect(() => {
    const errCode = searchParams.get('error');
    if (errCode) {
      const label = ERROR_LABELS[errCode] || errCode;
      const detail = searchParams.get('detail');
      toast.error(detail ? `${label} - ${detail}` : label, { duration: 8000 });
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, password, name };
      const res = await api.post(endpoint, payload);
      setUser(res.data);
      toast.success(isLogin ? 'Connexion reussie !' : 'Compte cree avec succes !');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const res = await api.get('/auth/google/authorize');
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast.error('Erreur de connexion Google');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-12">
          <img src={LOGO_URL} alt="ViralSynth" className="w-64 h-auto" />
        </Link>

        <div className="card-surface rounded-lg p-8 md:p-10">
          <h1 className="font-heading text-2xl font-medium text-white mb-2 text-center">
            {isLogin ? 'Connexion' : 'Creer un compte'}
          </h1>
          <p className="text-sm text-white/40 text-center mb-8">
            {isLogin ? 'Accedez a votre dashboard' : 'Lancez votre chaine IA en 3 minutes'}
          </p>

          {/* Google Auth */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-auth-btn"
            className="w-full flex items-center justify-center gap-3 py-3 rounded-md border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-all mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continuer avec Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30 uppercase tracking-wider">ou</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Email form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-xs text-white/50 mb-2">Nom complet</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 input-dark rounded-md text-sm"
                    placeholder="Jean Dupont"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-white/50 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-md text-sm"
                  placeholder="vous@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-md text-sm"
                  placeholder="Min. 6 caracteres"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-accent py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Se connecter' : 'Creer mon compte'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            {isLogin && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs text-white/30 hover:text-[#FF3300] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublie ?
                </button>
              </div>
            )}
          </form>
        </div>

        <p className="text-center text-sm text-white/40 mt-6">
          {isLogin ? "Pas encore de compte ?" : "Deja un compte ?"}
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              navigate(isLogin ? '/register' : '/login', { replace: true });
            }}
            className="text-[#FF3300] hover:text-[#FF3300]/80 ml-1 transition-colors"
            data-testid="toggle-auth-mode"
          >
            {isLogin ? "Creer un compte" : "Se connecter"}
          </button>
        </p>
      </div>
    </div>
  );
}
